import { FC, Fragment } from "preact/compat";
import { Listbox, Transition } from "@headlessui/react";
import {
    SelectContentProps,
    SelectItemProps,
    SelectProps,
    SelectTriggerProps,
} from "./interfaces";
import cn from "classnames";

const Select = <TValue,>({
    value,
    children,
    classNames,
    onChange,
    ...props
}: SelectProps<TValue>) => (
    <Listbox value={value} onChange={onChange} {...props}>
        <div className={cn("relative", classNames?.wrapper)}>{children}</div>
    </Listbox>
);

const SelectTrigger: FC<SelectTriggerProps> = ({
    title,
    className,
    badge,
    children,
    disabled,
}) => (
    <Listbox.Button
        title={title}
        className={cn(
            "flex w-full items-center justify-between rounded-3xl border border-gray-300 fill-current px-4 py-1 text-left outline-none truncate",
            "focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-white",
            disabled ? "bg-gray-100" : "bg-white",
            className,
        )}
    >
        {children}
        {badge && (
            <span className="absolute bottom-1 right-3 inline-block size-3 rounded-full border-2 border-white bg-[#096b9e]" />
        )}
    </Listbox.Button>
);

const SelectContent: FC<SelectContentProps> = ({ className, children }) => (
    <Transition
        as={Fragment}
        enterFrom="opacity-0 translate-y-2"
        enter="transition ease-in-out duration-200"
        leave="transition ease-in duration-100"
        leaveTo="opacity-0 translate-y-2"
    >
        <Listbox.Options
            className={cn(
                "overflow-y-overlay absolute z-30 mt-1 max-h-60 w-full rounded-2xl border border-gray-300 bg-white shadow-lg focus:outline-sky-500",
                className,
            )}
        >
            {children}
        </Listbox.Options>
    </Transition>
);

const SelectItem = <TValue,>({
    className,
    ...props
}: SelectItemProps<TValue>) => (
    <Listbox.Option
        className={(args) =>
            cn(
                "flex cursor-pointer items-center justify-between px-2 py-1",
                args.active && "bg-sky-100",
                typeof className === "function" ? className(args) : className,
            )
        }
        {...props}
    />
);

export { Select, SelectTrigger, SelectContent, SelectItem };
